import React, { useState, useCallback } from 'react';
import { resolveColor } from '../utils/colorResolver';
import { formatDescripcionSecciones } from '../utils/formatDescripcion';
import {
  Dialog,
  Box,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaletteIcon from '@mui/icons-material/Palette';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { CatalogoImageCarousel } from './CatalogoImageCarousel';
import { useProductoDetalle } from '../hooks/useProductoDetalle';
import { ProductoCatalogoPublico, ColorDetallePublico, ImagenCatalogo } from '../types';

// ─── Paleta (diseño_modal_inventario_producto.json) ───────────────────────────
const C = {
  primario: '#8A3F7D',
  primarioOscuro: '#3B183B',
  primarioSuave: '#F5EAF4',
  grisFondo: '#F6F6F7',
  grisBorde: '#E7E1E7',
  textoPrincipal: '#2E1B2F',
  textoSecundario: '#746B75',
  verde: '#2E8B72',
  verdeFondo: '#E6F6F1',
  rojo: '#D9363E',
  rojoFondo: '#FFE7EA',
  amarillo: '#C98500',
  amarilloFondo: '#FFF2D8',
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcularEstado(stock: number): 'Disponible' | 'Bajo' | 'Agotado' {
  if (stock === 0) return 'Agotado';
  if (stock < 1000) return 'Bajo';
  return 'Disponible';
}

const ESTADO_STYLES: Record<string, { background: string; color: string }> = {
  Disponible: { background: C.verdeFondo, color: C.verde },
  Bajo: { background: C.amarilloFondo, color: C.amarillo },
  Agotado: { background: C.rojoFondo, color: C.rojo },
};

function formatPrecioCOP(precio: number | null): string {
  if (precio === null) return '—';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(precio);
}

function formatFechaHora(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const fecha = d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const hora = d.toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${fecha}, ${hora}`;
  } catch {
    return iso;
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface ProductoDetalleModalProps {
  open: boolean;
  producto: ProductoCatalogoPublico | null;
  onClose: () => void;
  onAgregar: (producto: ProductoCatalogoPublico, imagenUrl: string | null) => void;
  selectedBolsaId: string | null;
  hideAgregar?: boolean;
}

// ─── Chip de estado ───────────────────────────────────────────────────────────
function EstadoChip({ stock }: { stock: number }) {
  const estado = calcularEstado(stock);
  const style = ESTADO_STYLES[estado];
  return (
    <Box
      data-testid="estado-chip"
      sx={{
        display: 'inline-block',
        px: 1.5,
        py: 0.25,
        borderRadius: '999px',
        fontSize: 12,
        fontWeight: 600,
        background: style.background,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {estado}
    </Box>
  );
}

// ─── Swatch de color ──────────────────────────────────────────────────────────
function ColorSwatch({ nombre, codigoColor, size = 20 }: { nombre: string; codigoColor: string | null; size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        backgroundColor: resolveColor({ nombre, codigoColor }),
        border: '1px solid #E7E1E7',
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

// ─── Card de métrica ──────────────────────────────────────────────────────────
function MetricaCard({
  label,
  valor,
  sufijo,
  icon,
  acento,
  valorFontSize,
}: {
  label: string;
  valor: string;
  sufijo?: string;
  icon: React.ReactNode;
  acento?: string;
  valorFontSize?: number;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        background: C.grisFondo,
        border: `1px solid ${C.grisBorde}`,
        borderRadius: '12px',
        p: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: C.textoSecundario }}>
        {icon}
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.textoSecundario, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: valorFontSize ?? 18, fontWeight: 800, color: acento ?? C.textoPrincipal, lineHeight: 1.2 }}>
        {valor}
        {sufijo && <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, color: C.textoSecundario, ml: 0.5 }}>{sufijo}</Typography>}
      </Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export function ProductoDetalleModal({
  open,
  producto,
  onClose,
  onAgregar,
  selectedBolsaId,
  hideAgregar = false,
}: ProductoDetalleModalProps) {
  const { detalle, loading } = useProductoDetalle(
    producto?.canonicoId ?? null,
    producto?.proveedorProductoId ?? null,
    open,
  );

  const [busqueda, setBusqueda] = useState('');
  const [colorFiltro, setColorFiltro] = useState<string | null>(null);
  const [colorSeleccionado, setColorSeleccionado] = useState<ColorDetallePublico | null>(null);
  const [activeImageUrl, setActiveImageUrl] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setBusqueda('');
    setColorFiltro(null);
    setColorSeleccionado(null);
    onClose();
  }, [onClose]);

  const handleAgregar = useCallback(() => {
    if (!producto) return;
    onAgregar(producto, activeImageUrl);
    handleClose();
  }, [producto, activeImageUrl, onAgregar, handleClose]);

  if (!producto) return null;

  // Datos: usamos detalle cuando está disponible, sino datos del card
  const colores: ColorDetallePublico[] = detalle?.colores ?? producto.coloresDisponibles.map(c => ({
    nombre: c.nombre,
    codigoColor: c.codigoColor ?? null,
    stock: c.stock,
    precio: producto.precioDesde,
    proveedor: '—',
  }));

  const imagenes: ImagenCatalogo[] = (detalle?.imagenes ?? producto.imagenes).map(img => ({
    url: img.url,
    orden: img.orden,
    color: img.color,
  }));

  // Filtrado de colores en la grilla
  const coloresFiltrados = colores.filter(c => {
    if (colorFiltro) return c.nombre === colorFiltro;
    if (busqueda.trim()) return c.nombre.toLowerCase().includes(busqueda.toLowerCase());
    return true;
  });

  const stockTotal = detalle?.stockTotal ?? producto.stockTotal;
  const precioDesde = detalle?.precioDesde ?? producto.precioDesde;
  const nombreProducto = detalle?.nombre ?? producto.nombre;
  const proveedorPrincipal = detalle?.proveedorPrincipal ?? producto.proveedorNombre ?? '—';
  const sku = detalle?.sku ?? producto.sku ?? '—';
  const descripcion = detalle?.descripcion ?? producto.descripcion;
  const lastSyncedRaw = detalle?.lastSynced ?? producto.lastSynced ?? null;
  const lastSynced = formatFechaHora(lastSyncedRaw);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      aria-label={nombreProducto}
      PaperProps={{
        sx: {
          width: '92vw',
          maxWidth: '1440px',
          maxHeight: '88vh',
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.22)',
          fontFamily: 'Inter, Nunito Sans, Arial, sans-serif',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      {/* Envoltorio vertical: fila de dos columnas (flex:1) + footer compartido debajo de
          AMBAS columnas (SPEC-179 · 3.5). El footer NO puede vivir dentro de una sola columna:
          rompe la simetría que hace que el borde inferior de la descripción (columna izquierda)
          coincida con el de "Inventario por color" (columna derecha). */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '88vh',
          overflow: 'hidden',
        }}
      >
      {/* Layout principal dos columnas */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '32% 1fr' },
          gap: '28px',
          p: '24px 24px 0 24px',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* ── Columna izquierda ───────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden', minHeight: 0 }}>
          {/* Carousel */}
          <Box
            sx={{
              border: `1px solid ${C.grisBorde}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: '#FFFFFF',
            }}
          >
            <CatalogoImageCarousel
              imagenes={imagenes}
              height={280}
              categoriaColor={C.primarioSuave}
              onCurrentImageChange={setActiveImageUrl}
            />
          </Box>

          {/* Info básica */}
          <Box
            sx={{
              border: `1px solid ${C.grisBorde}`,
              borderRadius: '12px',
              p: '16px',
              background: '#FFFFFF',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.textoPrincipal, mb: 1 }}>
              {nombreProducto}
            </Typography>
            {descripcion && descripcion.trim() ? (
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mb: 1.5 }}>
                <Typography
                  data-testid="modal-descripcion-producto"
                  sx={{
                    fontSize: 12,
                    color: C.textoSecundario,
                    lineHeight: 1.15,
                    whiteSpace: 'pre-line',
                    '& strong': { fontWeight: 700 },
                  }}
                >
                  {formatDescripcionSecciones(descripcion)}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography
                  data-testid="modal-descripcion-vacia"
                  sx={{
                    fontSize: 12,
                    color: 'text.disabled',
                    fontStyle: 'italic',
                    mb: 1.5,
                    lineHeight: 1.15,
                  }}
                >
                  Sin descripción
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0 }} />
              </>
            )}
            <Stack spacing={0.75}>
              {[
                { label: 'SKU', valor: sku },
              ].map(item => (
                <Box key={item.label} sx={{ display: 'flex', gap: 1 }}>
                  <Typography sx={{ fontSize: 12, color: C.textoSecundario, minWidth: 120 }}>{item.label}:</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textoPrincipal }}>{item.valor}</Typography>
                </Box>
              ))}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              disabled
              sx={{ mt: 2, borderColor: C.grisBorde, color: C.textoSecundario, fontSize: 11, borderRadius: '10px', textTransform: 'none' }}
            >
              Ver historial de sincronizaciones
            </Button>
          </Box>
        </Box>

        {/* ── Columna derecha ─────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden', minWidth: 0 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: C.primarioOscuro, lineHeight: 1.1 }}>
                {nombreProducto}
              </Typography>
              <Typography sx={{ fontSize: 14, color: C.textoSecundario, mt: 0.25 }}>
                Comparación de precios entre proveedores
              </Typography>
            </Box>
            <IconButton
              data-testid="modal-close-btn"
              onClick={handleClose}
              size="small"
              sx={{ color: C.textoSecundario, mt: -0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: C.textoSecundario }}>
              <CircularProgress size={14} sx={{ color: C.primario }} />
              <Typography sx={{ fontSize: 12 }}>Cargando detalle…</Typography>
            </Box>
          )}

          {/* 4 Métricas */}
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <MetricaCard
              label="Precio unitario"
              valor={formatPrecioCOP(precioDesde)}
              sufijo="COP"
              icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
              acento={C.primario}
            />
            <MetricaCard
              label="Stock total"
              valor={stockTotal.toLocaleString('es-CO')}
              icon={<InventoryIcon sx={{ fontSize: 14 }} />}
              acento={C.verde}
            />
            <MetricaCard
              label="Última sync"
              valor={lastSynced}
              icon={<RefreshIcon sx={{ fontSize: 14 }} />}
              valorFontSize={12}
            />
            <MetricaCard
              label="Colores disponibles"
              valor={colores.length.toString()}
              icon={<PaletteIcon sx={{ fontSize: 14 }} />}
              acento={C.verde}
            />
          </Box>

          {/* Resumen por color */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textoPrincipal, mb: 1 }}>
              Resumen por color
            </Typography>
            <Box
              data-testid="color-summary"
              sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}
            >
              {colores.map(c => (
                <Box
                  key={c.nombre}
                  data-testid="color-bolita"
                  onClick={() => setColorFiltro(prev => prev === c.nombre ? null : c.nombre)}
                  sx={{ cursor: 'pointer', textAlign: 'center', minWidth: 52, userSelect: 'none' }}
                >
                  <Tooltip title={`${c.nombre} — stock ${c.stock.toLocaleString('es-CO')}`} arrow>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        backgroundColor: resolveColor(c),
                        border: colorFiltro === c.nombre
                          ? `3px solid ${C.primario}`
                          : `2px solid ${C.grisBorde}`,
                        mx: 'auto',
                        mb: 0.4,
                        transition: 'border 0.15s',
                      }}
                    />
                  </Tooltip>
                  <Typography sx={{ fontSize: 10, color: C.textoSecundario, lineHeight: 1.2 }}>
                    {c.nombre}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: C.textoPrincipal, fontWeight: 600 }}>
                    {c.stock.toLocaleString('es-CO')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Grilla de inventario */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textoPrincipal }}>
                Inventario por color{proveedorPrincipal !== '—' ? ` (${proveedorPrincipal})` : ''}
              </Typography>
              <TextField
                size="small"
                placeholder="Buscar color..."
                value={busqueda}
                onChange={e => { setBusqueda(e.target.value); setColorFiltro(null); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: C.textoSecundario }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 200,
                  '& .MuiOutlinedInput-root': {
                    fontSize: 13,
                    borderRadius: '8px',
                    '& fieldset': { borderColor: C.grisBorde },
                  },
                }}
              />
            </Box>
            <Box
              data-testid="inventario-grilla"
              sx={{
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                border: `1px solid ${C.grisBorde}`,
                borderRadius: '10px',
                isolation: 'isolate',
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Color', 'Vista', 'Proveedor', 'Precio (COP)', 'Stock', 'Estado', 'Acciones'].map(col => (
                      <TableCell
                        key={col}
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: C.textoPrincipal,
                          background: C.grisFondo,
                          borderBottom: `1px solid ${C.grisBorde}`,
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coloresFiltrados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: C.textoSecundario, fontSize: 13 }}>
                        No se encontraron colores que coincidan con la búsqueda
                      </TableCell>
                    </TableRow>
                  ) : (
                    coloresFiltrados.map(c => (
                      <TableRow
                        key={c.nombre}
                        onClick={() => setColorSeleccionado(prev => prev?.nombre === c.nombre ? null : c)}
                        sx={{
                          cursor: 'pointer',
                          background: colorSeleccionado?.nombre === c.nombre ? C.primarioSuave : 'transparent',
                          '&:hover': { background: C.grisFondo },
                        }}
                      >
                        <TableCell sx={{ fontSize: 14, color: C.textoPrincipal }}>{c.nombre}</TableCell>
                        <TableCell>
                          <ColorSwatch nombre={c.nombre} codigoColor={c.codigoColor} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, color: C.textoSecundario }}>{c.proveedor}</TableCell>
                        <TableCell sx={{ fontSize: 14, textAlign: 'right', color: C.textoPrincipal, fontWeight: 600 }}>
                          {formatPrecioCOP(c.precio)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 14, textAlign: 'right', color: C.textoPrincipal }}>
                          {c.stock.toLocaleString('es-CO')}
                        </TableCell>
                        <TableCell>
                          <EstadoChip stock={c.stock} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <IconButton size="small" sx={{ color: C.textoSecundario }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer — fuera de ambas columnas (SPEC-179 · 3.5): comparte el mismo borde inferior
          real del modal, en vez de vivir dentro de la columna derecha únicamente. */}
      <Box
        data-testid="modal-footer"
        sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, p: '12px 24px 24px 24px', flexShrink: 0 }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          startIcon={<CloseIcon />}
          sx={{
            borderColor: C.grisBorde,
            color: C.textoSecundario,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { borderColor: C.primario, color: C.primario },
          }}
        >
          Cerrar
        </Button>
        {!hideAgregar && (
          <Button
            variant="contained"
            disabled={!selectedBolsaId}
            onClick={handleAgregar}
            startIcon={<AddIcon />}
            sx={{
              background: C.primario,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { background: C.primarioOscuro },
              '&.Mui-disabled': { background: C.grisBorde, color: C.textoSecundario },
            }}
          >
            Agregar
          </Button>
        )}
      </Box>
      </Box>
    </Dialog>
  );
}

